// schema
import * as ISchema from "@schema";
// libraries
import React from "react";
import { useDispatch } from "react-redux";

// components
import { WebSettingsContext, TeamSections } from "../web-settings";
import { TeamSearchInput } from "@components/team-search-input";

// util
import { track, TrackingEvents } from "@util/analytics.util";
import { logger } from "@util/logger.util";

// mutations
import { updateTeam } from "@mutations";

// state
import { getCurrentEnvironment } from "@src/state/relay-environment";
import { snackBarOpen } from "@actions/snack-bar.actions";

// styles
import { greys } from "@clockwise/web-commons/src/styles/color.styles";

// material-ui
import { Button } from "@clockwise/design-system";
import { Typography } from "@material-ui/core";
import { Edit } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";

// ~-~-~-~-~-~-~-
// Team Name Settings
// ~-~-~-~-~-~-~-
interface TeamNameProps {
  org: ISchema.IOrg;
  team: ISchema.ITeam;
}

const useStyles = makeStyles({
  content: {
    marginBottom: 30,
  },
  viewTeamNameContainer: {
    display: "flex",
    alignItems: "center",
  },
  teamName: {
    marginRight: 10,
    color: greys.slate,
  },
});

export const TeamName = (props: TeamNameProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { webSettingsRefetch } = React.useContext(WebSettingsContext);

  const [edit, setEdit] = React.useState(false);
  const [teamName, setTeamName] = React.useState(props.team.teamName);
  const [isValid, setIsValid] = React.useState(true);

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  const onTeamNameChange = (newTeamName: string, teamNameIsValid: boolean) => {
    setTeamName(newTeamName);
    setIsValid(teamNameIsValid);
  };

  const saveTeamName = () => {
    updateTeam(
      getCurrentEnvironment(),
      {
        orgRelayId: props.org.id,
        teamId: props.team.teamId,
        newTeamName: teamName,
      },
      () => {
        dispatch(snackBarOpen("Updated team name"));
        setEdit(false);
        webSettingsRefetch();
      },
      () => {
        dispatch(snackBarOpen("Failed to update team name"));
        logger.error("Failed to update team name");
      },
    );
    track(TrackingEvents.TEAMS.TEAM_SETTINGS_ADMIN_SET_TEAM_NAME);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  const { org, team } = props;

  return (
    <div cw-id={`web-settings-${TeamSections.TeamName}`} className={classes.content}>
      <Typography variant="h3" className="cw-mb-4">
        Team name
      </Typography>
      {edit ? (
        <div className="cw-flex cw-gap-2 cw-items-stretch">
          <TeamSearchInput org={org} onChange={onTeamNameChange} initialTeamName={team.teamName} />
          <Button size="large" variant="outlined" onClick={() => setEdit(false)}>
            Cancel
          </Button>
          <Button size="large" sentiment="positive" onClick={saveTeamName} disabled={!isValid}>
            Save
          </Button>
        </div>
      ) : (
        <div className={classes.viewTeamNameContainer}>
          <div className={classes.teamName}>{team.teamName}</div>
          <Button variant="text" startIcon={Edit} onClick={() => setEdit(true)} />
        </div>
      )}
    </div>
  );
};
